/**
 * InsightsTab Component
 * TASK-395: Preparation Screen — AI Chat + Tabbed Panel
 *
 * Displays AI-extracted insights from insightService.extractInsights().
 * Color-coded cards grouped by category (action_needed, alert, positive, neutral).
 * Expandable source citations per insight.
 *
 * Receives Insight[] from prepSearch.insights (AI-generated),
 * NOT raw ClassifiedResult[] from the search classifier.
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Lightbulb,
  Loader2,
  RefreshCw,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Mail,
  FileText,
  Newspaper,
  StickyNote,
  Calendar,
  TrendingUp,
} from 'lucide-react';
import type { Insight, InsightCategory, SourceCitation } from '../../../types/dataGathering';

// ============================================================================
// Types
// ============================================================================

interface MeetingInsightsTabProps {
  isLoading: boolean;
  isComplete: boolean;
  hasError: boolean;
  isExtractingInsights: boolean;
  insights: Insight[];
  summary: string;
  sourceSummary: {
    type: string;
    count: number;
    status: 'loading' | 'completed' | 'error';
  }[];
  onRetry: () => void;
}

// ============================================================================
// Category Config
// ============================================================================

const CATEGORY_CONFIG: Record<InsightCategory, {
  label: string;
  bg: string;
  border: string;
  text: string;
  icon: React.ReactNode;
  order: number;
}> = {
  action_needed: {
    label: 'Action Needed',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-900',
    icon: <AlertCircle className="w-5 h-5 text-red-500" />,
    order: 0,
  },
  alert: {
    label: 'Alerts',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    text: 'text-orange-900',
    icon: <AlertTriangle className="w-5 h-5 text-orange-500" />,
    order: 1,
  },
  positive: {
    label: 'Positive Findings',
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-900',
    icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
    order: 2,
  },
  neutral: {
    label: 'Context',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-900',
    icon: <Lightbulb className="w-5 h-5 text-blue-500" />,
    order: 3,
  },
};

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  email: <Mail className="w-3.5 h-3.5" />,
  news: <Newspaper className="w-3.5 h-3.5" />,
  notes: <StickyNote className="w-3.5 h-3.5" />,
  calendar: <Calendar className="w-3.5 h-3.5" />,
  document: <FileText className="w-3.5 h-3.5" />,
  market_news: <TrendingUp className="w-3.5 h-3.5" />,
  meeting_notes: <StickyNote className="w-3.5 h-3.5" />,
  life_events: <Lightbulb className="w-3.5 h-3.5" />,
};

// ============================================================================
// Helpers
// ============================================================================

function groupByCategory(insights: Insight[]): Map<InsightCategory, Insight[]> {
  const groups = new Map<InsightCategory, Insight[]>();
  for (const insight of insights) {
    const cat = insight.category || 'neutral';
    const list = groups.get(cat) || [];
    list.push(insight);
    groups.set(cat, list);
  }
  return groups;
}

function formatSourceDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

// ============================================================================
// Component
// ============================================================================

const MeetingInsightsTab: React.FC<MeetingInsightsTabProps> = ({
  isLoading,
  isComplete,
  hasError,
  isExtractingInsights,
  insights,
  summary,
  sourceSummary,
  onRetry,
}) => {
  const completedCount = sourceSummary.filter(s => s.status === 'completed').length;
  const totalCount = sourceSummary.length;
  // Only show "Unable to gather intelligence" when ALL sources failed AND no insights arrived.
  // Insights from the orchestrator can succeed even when search sources time out.
  const allFailed = totalCount > 0 && sourceSummary.every(s => s.status === 'error') && insights.length === 0;
  const grouped = groupByCategory(insights);

  // Sort category groups by priority order
  const sortedCategories = Array.from(grouped.entries())
    .sort(([a], [b]) => (CATEGORY_CONFIG[a]?.order ?? 99) - (CATEGORY_CONFIG[b]?.order ?? 99));

  return (
    <div className="h-full overflow-y-auto">
      {/* Header with source status */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Gathered Intelligence</h2>
          {hasError && !allFailed && (
            <button
              onClick={onRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-md"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          )}
        </div>

        {/* Source badges — muted style for errors */}
        <div className="flex flex-wrap gap-2">
          {sourceSummary.map((source) => (
            <span
              key={source.type}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                source.status === 'completed'
                  ? 'bg-green-50 text-green-700'
                  : source.status === 'loading'
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {source.status === 'loading' && (
                <Loader2 className="w-3 h-3 animate-spin" />
              )}
              {source.type}: {source.count}
            </span>
          ))}
        </div>

        {/* Partial source note */}
        {!isLoading && !allFailed && completedCount < totalCount && totalCount > 0 && (
          <p className="text-xs text-gray-400 mt-2">
            {completedCount} of {totalCount} sources loaded. Some data may be missing.
          </p>
        )}
      </div>

      {/* Summary header */}
      {summary && !isLoading && insights.length > 0 && (
        <div className="px-6 py-3 bg-amber-50/60 border-b border-amber-100">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm font-medium text-amber-900">{summary}</span>
          </div>
        </div>
      )}

      {/* Loading state — only when no insights yet */}
      {isLoading && insights.length === 0 && !isExtractingInsights && (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Searching for insights...</p>
          </div>
        </div>
      )}

      {/* Extracting insights phase */}
      {isExtractingInsights && insights.length === 0 && (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-3 animate-pulse" />
            <p className="text-sm text-gray-500">Analyzing findings...</p>
          </div>
        </div>
      )}

      {/* All sources failed */}
      {allFailed && !isLoading && (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-3">Unable to gather intelligence</p>
            <button
              onClick={onRetry}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry All
            </button>
          </div>
        </div>
      )}

      {/* Insight cards grouped by category */}
      {sortedCategories.length > 0 && (
        <div className="p-6 space-y-6">
          {sortedCategories.map(([category, items]) => {
            const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.neutral;
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3">
                  {config.icon}
                  <h3 className="text-sm font-semibold text-gray-800">{config.label}</h3>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
                    {items.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {items.map((insight) => (
                    <InsightCard key={insight.id} insight={insight} config={config} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !isExtractingInsights && insights.length === 0 && !allFailed && (
        <div className="text-center py-12 px-6">
          <Lightbulb className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-3">
            {isComplete
              ? 'No notable findings from available sources.'
              : 'Insights will appear once data gathering completes.'}
          </p>
          {!isComplete && (
            <button
              onClick={onRetry}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
            >
              <RefreshCw className="w-4 h-4" />
              Start Search
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Insight Card — color-coded with expandable sources
// ============================================================================

interface InsightCardProps {
  insight: Insight;
  config: typeof CATEGORY_CONFIG[InsightCategory];
}

const InsightCard: React.FC<InsightCardProps> = ({ insight, config }) => {
  const [expanded, setExpanded] = useState(false);
  const hasSources = insight.sources && insight.sources.length > 0;
  const sourceCount = insight.sources?.length || 0;

  return (
    <div className={`rounded-lg border ${config.border} ${config.bg} transition-colors`}>
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {config.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${config.text}`}>{insight.title}</p>
            {insight.description && (
              <p className={`text-sm mt-1 ${config.text} opacity-80`}>{insight.description}</p>
            )}
            {hasSources && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 mt-2 text-xs text-gray-500 hover:text-gray-700"
              >
                {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {sourceCount} source{sourceCount !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expandable source citations */}
      {expanded && hasSources && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-200/50">
          <div className="space-y-2 ml-8">
            {insight.sources!.map((source, idx) => (
              <SourceCard key={`${source.id}-${idx}`} source={source} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Source Citation Card
// ============================================================================

const SourceCard: React.FC<{ source: SourceCitation }> = ({ source }) => {
  const dateStr = formatSourceDate(source.date);

  return (
    <div className="flex items-start gap-2 text-xs">
      <div className="flex-shrink-0 mt-0.5 text-gray-400">
        {SOURCE_ICONS[source.type] || <FileText className="w-3.5 h-3.5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-gray-700">{source.title}</span>
          {dateStr && (
            <>
              <span className="text-gray-300">&mdash;</span>
              <span className="text-gray-400">{dateStr}</span>
            </>
          )}
        </div>
        {source.excerpt && (
          <p className="text-gray-500 mt-0.5 line-clamp-2">{source.excerpt}</p>
        )}
      </div>
    </div>
  );
};

export default MeetingInsightsTab;
